import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  Pagination,
  Button,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { dashboardService } from '../../../services/dashboard-service';
import { toast } from 'react-toastify';

// High-fidelity fallback search results matching backend DTO
const mockUserSearchResult = {
  totalCount: 7,
  users: [
    { userId: "usr_9281a", externalId: "ext_firebase_12", accountId: "acc_premium_01", role: "ACCOUNT_OWNER", status: "ACTIVE", email: "johndoe@school.edu", name: "John Doe", createdAt: "2026-04-12T10:30:00Z" },
    { userId: "usr_3847b", externalId: "ext_firebase_34", accountId: "acc_trial_02", role: "READER", status: "ACTIVE", email: "reader_alice@school.edu", name: "Alice Cooper", createdAt: "2026-05-18T14:45:00Z" },
    { userId: "usr_1029c", externalId: "ext_firebase_56", accountId: "acc_premium_01", role: "READER", status: "SUSPENDED", email: "reader_bob@school.edu", name: "Bob Marley", createdAt: "2026-05-20T09:12:00Z" },
    { userId: "usr_7726e", externalId: "ext_firebase_90", accountId: "acc_trial_02", role: "ACCOUNT_OWNER", status: "LOCKED", email: "charlie@school.edu", name: "Charlie Chaplin", createdAt: "2026-05-22T16:00:00Z" },
    { userId: "usr_5561f", externalId: "ext_firebase_11", accountId: "acc_premium_04", role: "READER", status: "BANNED", email: "reader_eve@school.edu", name: "Eve Online", createdAt: "2026-05-24T11:20:00Z" },
    { userId: "usr_4432g", externalId: "ext_firebase_22", accountId: "acc_premium_04", role: "READER", status: "DORMANT", email: "reader_dan@school.edu", name: "Dan Smith", createdAt: "2025-11-15T15:30:00Z" }
  ]
};

const UserManagementTab = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isMocked, setIsMocked] = useState(false);

  // Search parameters state
  const [term, setTerm] = useState('');
  const [role, setRole] = useState('ALL');
  const [status, setStatus] = useState('ALL');
  const [page, setPage] = useState(1);
  const size = 5; // Page size

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const query = {
        term: term.trim() ? term.trim() : null,
        role: role === 'ALL' ? null : role,
        status: status === 'ALL' ? null : status,
        page: page - 1,
        size: size
      };

      const result = await dashboardService.searchUsers(query);
      setUsers(result.users || []);
      setTotalCount(result.totalCount || 0);
      setIsMocked(false);
    } catch (err) {
      console.warn("Using offline mock data fallback for user search query:", err.message);
      setIsMocked(true);
      // Filter mock data locally to maintain a premium simulation experience
      let filtered = [...mockUserSearchResult.users];
      
      if (term.trim()) {
        const queryTerm = term.toLowerCase().trim();
        filtered = filtered.filter(u => 
          u.userId.toLowerCase().includes(queryTerm) ||
          u.accountId.toLowerCase().includes(queryTerm) ||
          (u.email && u.email.toLowerCase().includes(queryTerm)) ||
          (u.name && u.name.toLowerCase().includes(queryTerm))
        );
      }
      
      if (role !== 'ALL') {
        filtered = filtered.filter(u => u.role === role);
      }
      
      if (status !== 'ALL') {
        filtered = filtered.filter(u => u.status === status);
      }

      const total = filtered.length;
      const start = (page - 1) * size;
      const paged = filtered.slice(start, start + size);
      
      setUsers(paged);
      setTotalCount(total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, role, status]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info(`Copied ID to clipboard: ${text}`);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Color chips maps matching M3 design
  const getRoleChipColor = (userRole) => {
    switch (userRole) {
      case 'PLATFORM_OWNER':
        return {
          backgroundColor: theme.palette.tertiaryContainer || '#f3e5f5',
          color: theme.palette.onTertiaryContainer || '#4a148c'
        };
      case 'ACCOUNT_OWNER':
        return {
          backgroundColor: theme.palette.secondaryContainer || '#e8f0fe',
          color: theme.palette.onSecondaryContainer || '#1a73e8'
        };
      case 'READER':
        return {
          backgroundColor: theme.palette.primaryContainer || '#e6f4ea',
          color: theme.palette.onPrimaryContainer || '#137333'
        };
      default:
        return {};
    }
  };

  const getStatusChipColor = (userStatus) => {
    switch (userStatus) {
      case 'ACTIVE':
        return {
          backgroundColor: 'rgba(76, 175, 80, 0.15)',
          color: theme.palette.success.main,
          borderColor: 'rgba(76, 175, 80, 0.3)'
        };
      case 'SUSPENDED':
      case 'LOCKED':
        return {
          backgroundColor: 'rgba(255, 152, 0, 0.15)',
          color: theme.palette.warning.main,
          borderColor: 'rgba(255, 152, 0, 0.3)'
        };
      case 'BANNED':
        return {
          backgroundColor: 'rgba(244, 67, 54, 0.15)',
          color: theme.palette.error.main,
          borderColor: 'rgba(244, 67, 54, 0.3)'
        };
      default:
        return {
          backgroundColor: theme.palette.surfaceContainerLow,
          color: theme.palette.text.secondary,
          borderColor: theme.palette.outlineVariant
        };
    }
  };

  return (
    <Box sx={{ animation: 'fadeIn 0.5s ease-out' }}>
      
      {/* Search Filter Header Card */}
      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent sx={{ p: 3 }}>
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={3} alignItems="center">
              
              <Grid item xs={12} sm={5} md={6}>
                <TextField
                  fullWidth
                  label="Search users"
                  placeholder="Search by User ID, email, account, or name..."
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="role-select-label">Role</InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={role}
                    label="Role"
                    onChange={(e) => { setRole(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Roles</MenuItem>
                    <MenuItem value="PLATFORM_OWNER">Platform Owner</MenuItem>
                    <MenuItem value="ACCOUNT_OWNER">Account Owner</MenuItem>
                    <MenuItem value="READER">Reader</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={status}
                    label="Status"
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="ALL">All Statuses</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                    <MenuItem value="LOCKED">Locked</MenuItem>
                    <MenuItem value="DORMANT">Dormant</MenuItem>
                    <MenuItem value="BANNED">Banned</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={1} md={2}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  startIcon={<FilterIcon />}
                  sx={{
                    height: '56px',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 700
                  }}
                >
                  Filter
                </Button>
              </Grid>

            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Results Datatable Card */}
      <Card sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.surfaceContainerHigh }}>
                <TableCell sx={{ fontWeight: 700 }}>USER ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>DISPLAY NAME</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>EMAIL ADDRESS</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ACCOUNT ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>ROLE</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>STATUS</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>CREATED AT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                      🔍 No platform users match your current filter parameters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.userId}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme.palette.surfaceContainerLow
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    
                    {/* User ID with Copy chip */}
                    <TableCell>
                      <Tooltip title="Click to copy User ID">
                        <Chip
                          label={user.userId}
                          size="small"
                          onClick={() => copyToClipboard(user.userId)}
                          onDelete={() => copyToClipboard(user.userId)}
                          deleteIcon={<CopyIcon style={{ fontSize: '14px' }} />}
                          sx={{ 
                            fontFamily: 'monospace', 
                            cursor: 'pointer',
                            borderRadius: '6px'
                          }}
                        />
                      </Tooltip>
                    </TableCell>

                    {/* Display Name */}
                    <TableCell sx={{ fontWeight: 600 }}>
                      {user.name || '-'}{isMocked && <span style={{ color: theme.palette.error.main, fontSize: '11px', marginLeft: '6px' }}>(Mocked)</span>}
                    </TableCell>

                    {/* Email */}
                    <TableCell sx={{ color: theme.palette.text.secondary }}>
                      {user.email || '-'}{isMocked && <span style={{ color: theme.palette.error.main, fontSize: '11px', marginLeft: '6px' }}>(Mocked)</span>}
                    </TableCell>

                    {/* Account ID Badge */}
                    <TableCell>
                      <Chip
                        label={user.accountId}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '6px',
                          borderColor: theme.palette.outlineVariant
                        }}
                      />
                    </TableCell>

                    {/* Role Chip */}
                    <TableCell>
                      <Chip
                        label={user.role.replace('_', ' ')}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          ...getRoleChipColor(user.role)
                        }}
                      />
                    </TableCell>

                    {/* Status Chip */}
                    <TableCell>
                      <Chip
                        label={user.status}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          fontSize: '11px',
                          borderRadius: '8px',
                          ...getStatusChipColor(user.status)
                        }}
                      />
                    </TableCell>

                    {/* Registration Date */}
                    <TableCell sx={{ color: theme.palette.text.secondary, fontSize: '13px' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>

                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalCount > size && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Pagination
              count={Math.ceil(totalCount / size)}
              page={page}
              onChange={(e, p) => setPage(p)}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>
        )}
      </Card>

    </Box>
  );
};

export default UserManagementTab;
